import { FormControl } from "@mui/material"
import Box from "@mui/material/Box"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import React from "react"
import { Controller, useFormContext } from "react-hook-form"

import { FIELD_ERROR_CLASSES, FIELD_LABEL_CLASSES, SELECT_STYLING_PROPS } from "@/constants/forms"
import { useGetLocations as useGetBankLocations } from "@/hooks/useGetLocations"
import { AddressFormat } from "@/types/common"
import { formatLocationAddress } from "@/utils/utils"

interface BankLocationSelectorProps {
  bankId?: string | null
  name?: string
  label?: string
  required?: boolean
  disabled?: boolean
}

const BankLocationSelector: React.FC<BankLocationSelectorProps> = ({
  bankId,
  name = "bankLocationId",
  label = "Bank Location",
  required = false,
  disabled = false,
}) => {
  const methods = useFormContext()

  const { locations: bankLocations, isLocationsFetching: isBankLocationsFetching } = useGetBankLocations(bankId || "", {
    isLocationActive: true,
  })

  const handleValueRender = (selected: unknown) => {
    if (!bankLocations?.length) {
      return <span className='text-gray-500'>Bank has no available locations</span>
    }

    if (!selected) {
      return <span className='text-gray-500'>Select One</span>
    }

    const selectedLocation = bankLocations.find(({ locationId }) => locationId === selected)
    return selectedLocation
      ? `${selectedLocation.description} - ${formatLocationAddress(selectedLocation, AddressFormat.AddressOnly)}`
      : String(selected)
  }

  const isDisabled = !bankId || isBankLocationsFetching || bankLocations?.length === 0 || disabled

  return (
    <Box className='space-y-2'>
      <Controller
        name={name}
        control={methods.control}
        rules={{ required: required ? `${label} is required` : false }}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth>
            <label className={FIELD_LABEL_CLASSES}>
              {label}
              {required && <span className='ml-1 text-red-600'>*</span>}
            </label>
            <Select
              {...SELECT_STYLING_PROPS}
              {...field}
              error={Boolean(error)}
              disabled={isDisabled}
              renderValue={handleValueRender}
            >
              {bankLocations.map((location) => (
                <MenuItem key={location.locationId} value={location.locationId}>
                  {location.description} - {formatLocationAddress(location, AddressFormat.AddressOnly)}
                </MenuItem>
              ))}
            </Select>
            {error && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
          </FormControl>
        )}
      />
      <p className='text-sm text-gray-700'>
        NOTE: Additional bank locations can be created in the banks location details
      </p>
    </Box>
  )
}

export default BankLocationSelector
